import { parseVariants, variantUrl, type Crop, type Format, type MediaForPicture } from "@/lib/media-shared";

type Props = {
  media: MediaForPicture;
  crop?: Crop;
  /** Wie breit das Bild im Layout ist, z. B. "(max-width: 800px) 100vw, 800px". Bestimmt die geladene Variante. */
  sizes: string;
  /** Leerer String für rein dekorative Bilder. Standard: Alt-Text aus der Mediathek. */
  alt?: string;
  /** Für das größte Bild oberhalb der Falz (LCP): sofort und mit hoher Priorität laden. */
  priority?: boolean;
  className?: string;
};

/** Responsives Bild als AVIF, WebP und JPEG-Fallback, ohne Client-JS. Rendert nichts, solange das Bild nicht verarbeitet ist. */
export function Picture({ media, crop = "orig", sizes, alt, priority = false, className }: Props) {
  const all = parseVariants(media.variants);
  const v = all[crop] ?? all.orig;
  if (!v || media.version === 0) return null;
  const used: Crop = all[crop] ? crop : "orig";
  const srcSet = (fmt: Format) => v.widths.map((w) => `${variantUrl(media, used, w, fmt)} ${w}w`).join(", ");
  const largest = v.widths.at(-1)!;
  const fallback = v.widths.find((w) => w >= 800) ?? largest;

  return (
    <picture className={className}>
      <source type="image/avif" srcSet={srcSet("avif")} sizes={sizes} />
      <source type="image/webp" srcSet={srcSet("webp")} sizes={sizes} />
      <img
        src={variantUrl(media, used, fallback, "jpg")}
        srcSet={srcSet("jpg")}
        sizes={sizes}
        width={largest}
        height={Math.round((largest * v.height) / v.width)}
        alt={alt ?? media.alt}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        decoding="async"
        style={media.blurDataUrl ? { backgroundImage: `url(${media.blurDataUrl})` } : undefined}
      />
    </picture>
  );
}

/** Bild mit Bildunterschrift und Bildnachweis. */
export function Figure({ media, caption, ...props }: Props & { media: MediaForPicture & { caption: string; credit: string }; caption?: string }) {
  const text = caption ?? media.caption;
  return (
    <figure className="fig">
      <Picture media={media} {...props} />
      {(text || media.credit) && (
        <figcaption>
          {text}
          {media.credit && <span className="fig-credit">Foto: {media.credit}</span>}
        </figcaption>
      )}
    </figure>
  );
}
